import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Dropdown } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import useThunkDispatch from 'packages/hooks/useThunkDispatch';
import useAccessRights from 'packages/hooks/useAccessRights';
import roles from 'packages/configs/roles';
import debounce from 'packages/helpers/debounce';
import { customerIdSelector } from 'packages/store/user/user.selectors';
import { Category } from 'packages/store/categories/types';
import { CollectionSubType, CollectionType } from 'packages/store/collections/types';
import { searchCategories } from 'packages/store/searchCategory/searchCategory.actions';
import { getSearchedCategoriesState } from 'packages/store/searchCategory/searchCategory.selectors';
import { OwnershipFilterType } from '../../filters/components/Ownership/types';
import CategoryOptionThumbnail from './CategoryOptionThumbnail';

type Props = {
    selected: Category[];
    onChange: (selected: Category[]) => void;
    disabledIds?: Category['id'][];
    triggerProps?: Record<string, string>;
    searchProps?: Record<string, string>;
};

export const MAX_CATEGORIES_PER_ADD = 100;

const SEARCH_DEBOUNCE_MS = 1000;
const type: CollectionType = 'CREATIVE';
const subType: CollectionSubType = 'TEMPLATE';

const CategoryMultiSelect = ({
    selected,
    onChange,
    disabledIds = [],
    triggerProps: extraTriggerProps,
    searchProps,
}: Props) => {
    const { translate } = useTranslate();
    const dispatch = useThunkDispatch();
    const currentCompany = useSelector(customerIdSelector);
    const { fetchOptions, categories, isFetching } = useSelector(getSearchedCategoriesState);
    const { isAccountRoleAssigned } = useAccessRights();
    const [searchValue, setSearchValue] = useState('');
    const fetchOptionsRef = useRef(fetchOptions);
    const isAdmin = isAccountRoleAssigned(roles.video_brand_studio.handler.admin);

    useEffect(() => {
        fetchOptionsRef.current = fetchOptions;
    }, [fetchOptions]);

    const onSearch = useMemo(
        () =>
            debounce(
                (newSearchValue: string) => {
                    dispatch(
                        searchCategories(
                            {
                                ...fetchOptionsRef.current,
                                sort: ['NAME', 'ASC'],
                                type,
                                subType,
                                size: 1000,
                                ownershipSource: isAdmin
                                    ? OwnershipFilterType.ALL
                                    : OwnershipFilterType.OWNED_BY_ANYONE,
                                includeEmptyCollection: true,
                                search: newSearchValue,
                            },
                            currentCompany,
                            true,
                        ),
                    );
                },
                SEARCH_DEBOUNCE_MS,
                false,
            ),
        [dispatch, currentCompany, isAdmin],
    );

    const handleSearchValueChange = useCallback(
        (newValue: string) => {
            setSearchValue(newValue);
            onSearch(newValue);
        },
        [onSearch],
    );

    const toggle = (category: Category) => {
        onChange(
            selected.some(({ id: selectedId }) => selectedId === category.id)
                ? selected.filter(({ id: selectedId }) => selectedId !== category.id)
                : [...selected, category],
        );
    };

    const isAtLimit = selected.length >= MAX_CATEGORIES_PER_ADD;

    const getTriggerText = () => {
        if (!selected.length) {
            return translate('modal.template.move.none');
        }

        if (selected.length === 1) {
            return selected[0].name;
        }

        return translate('modal.template.add.selected', { count: selected.length });
    };

    return (
        <Dropdown
            position="bottom"
            trigger={({ isOpen, ...triggerProps }) => (
                <Button
                    isFullWidth
                    isPressed={isOpen}
                    rightIcon={<Dropdown.Arrow />}
                    {...triggerProps}
                    aria-label={translate('modal.template.move.dropdown.category')}
                    {...extraTriggerProps}
                >
                    <Dropdown.SelectButtonText>{getTriggerText()}</Dropdown.SelectButtonText>
                </Button>
            )}
        >
            <Dropdown.SearchInput
                value={searchValue}
                onChange={handleSearchValueChange}
                placeholder={translate('modal.template.move.search.category')}
                aria-label={translate('modal.template.move.search.category')}
                {...searchProps}
            />
            <Dropdown.SectionTitle> </Dropdown.SectionTitle>
            {isAtLimit && (
                <Dropdown.Text>
                    {translate('modal.template.add.categories-limit', { count: MAX_CATEGORIES_PER_ADD })}
                </Dropdown.Text>
            )}
            {isFetching && (
                <Dropdown.Item
                    onClick={() => null}
                    isDisabled
                    icon={
                        <Button
                            isLoading
                            variant="clean"
                            aria-label={translate('modal.template.move.search.loading')}
                        />
                    }
                >
                    <Dropdown.SelectButtonText>
                        {translate('modal.template.move.fetch.category')}
                    </Dropdown.SelectButtonText>
                </Dropdown.Item>
            )}
            {categories?.length === 0 && !isFetching && (
                <Dropdown.Text>{translate('modal.template.move.results.none')}</Dropdown.Text>
            )}
            {categories?.map((category) => {
                const isAssigned = disabledIds.includes(category.id);
                const isSelected = selected.some(({ id: selectedId }) => selectedId === category.id);

                return (
                    <Dropdown.Item
                        key={category.id}
                        onClick={() => toggle(category)}
                        checkedVariant="checkbox"
                        isChecked={isAssigned || isSelected}
                        isDisabled={isAssigned || (isAtLimit && !isSelected)}
                        icon={<CategoryOptionThumbnail category={category} />}
                    >
                        {category.name}
                    </Dropdown.Item>
                );
            })}
        </Dropdown>
    );
};

export default CategoryMultiSelect;
